import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Link from 'next/link';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import Container from '../components/container';
import config from '../lib/config';

import { GetStaticProps } from 'next';
import { fetchAPI, fetchData, getPrimaryMenuItems } from '../lib/api';
import { H2, H3, Muted } from '../components/text';
import { IconButton } from '../components/icon-button';
import { MatchCard } from '../components/match-card';

import { ISponsor } from '../models/sponsor';
import { IPage } from '../models/page';
import { IMenuItem } from '../models/menu-item';
import { IMatchRoundData } from '../models/matchRoundData';
import { getDateFormat } from '../utils/date';
import { ICalendar, useRoundsAndCalendars } from '@/utils/useRoundsAndCalendars';

interface IProps {
	matchRoundsData: IMatchRoundData;
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	pageData: IPage;
	calendars: ICalendar[];
}

export default function ProgrammaPage({
	primaryMenuItems,
	pageData: { page },
	sponsors,
	matchRoundsData,
	calendars,
}: IProps) {
	const pageTitle = `${page.title} | ${config.siteName}`;
	const meta = page.seo?.description || page.title;

	const { roundData, currentRound, setCurrentRound, roundsCount } = useRoundsAndCalendars(matchRoundsData, calendars);

	const getRoundsOptions = () => {
		let rounds = [];

		for (let i = 1; i <= roundsCount; i++) {
			rounds.push(<option key={i} value={i}>{`Speelronde ${i}`}</option>);
		}

		return rounds;
	};

	const handleRoundChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
		setCurrentRound(parseInt(event.target.value));
	};

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />

			<Container className="py-12 xl:py-16 ">
				{roundData && (
					<article>
						<H2 className="text-center py-6">{page.title}</H2>

						<div className="pb-5 sm:flex sm:items-end sm:justify-between gap-4">
							<div className="flex-1">
								<div className="flex">
									<div className="flex flex-col">
										<H3 className="mb-1">Speelronde {roundData.currentRound}</H3>
										{roundData.start_date && roundData.end_date && (
											<Muted className="font-bold">
												{getDateFormat(roundData.start_date)} -{' '}
												{getDateFormat(roundData.end_date)}
											</Muted>
										)}
									</div>
								</div>
							</div>

							<div className="flex-1 mt-4">
								<div className="flex justify-center sm:justify-end gap-4">
									<div className="flex-1">
										<select
											id="Round"
											value={currentRound}
											onChange={handleRoundChange}
											className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10  ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600"
										>
											{getRoundsOptions()}
										</select>
									</div>
									<div className="mt-1 flex-1 flex justify-end">
										<IconButton
											icon="arrow-left"
											className="mr-4"
											onClick={() => setCurrentRound(roundData.prevRound)}
											disabled={!roundData.prevRound}
										/>
										<IconButton
											icon="arrow-right"
											onClick={() => setCurrentRound(roundData.nextRound)}
											disabled={!roundData.nextRound}
										/>
									</div>
								</div>
							</div>
						</div>
						<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
							{roundData?.matches?.map((match, index) => (
								<Link href={`/wedstrijden/${match.slug}`} key={index}>
									<MatchCard
										venue={match.venue}
										date={match.date}
										time={match.time}
										homeContestant={match.homeContestant}
										awayContestant={match.awayContestant}
										status={match.status}
										scores={match.scores}
										channel={match.channel}
										snug
										className="cursor-pointer hover:shadow-lg transition-shadow duration-300"
									/>
								</Link>
							))}
						</div>
					</article>
				)}
			</Container>
		</Layout>
	);
}

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const pageData = await fetchAPI<{ sponsors: { nodes: any[] } }>(
		`
		query {
			page(id: 79936, idType: DATABASE_ID) {
				title
				content
			}
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	let matchRoundsData: IMatchRoundData | null = null;

	try {
		const response = await fetch(`${config.apiUrl}/wp-json/statsperform/v1/current-round`);
		matchRoundsData = await response.json();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	let calendars: ICalendar[] | null = null;

	const fetchCalenders = async () => {
		const endpoint = `${config.apiUrl}/wp-json/statsperform/v1/available-calendars`;
		return fetchData(endpoint);
	};

	try {
		calendars = await fetchCalenders();
	} catch (error) {
		console.error('Error fetching data:', error);
	}

	return {
		props: {
			sponsors: pageData.sponsors.nodes,
			pageData,
			preview,
			primaryMenuItems,
			matchRoundsData,
			calendars,
		},
		revalidate: 300,
	};
};
